import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ApplyTravelerCaptureDto, TravelerDocumentMatchDto, TravelerResponseDto } from '@bandido/shared/dtos';
import { TRAVELER_CAPTURE_PROFILE_FIELDS } from '@bandido/shared/types';
import type { TravelerCaptureProposal, TravelerCaptureSource } from '@bandido/shared/types';
import { useAllCaseTravelers, useTravelerDocumentMatches } from '@frontend/features/travelers/queries';
import { travelerDisplayName, maskDocumentNumber } from '@frontend/features/travelers/travelerForm';
import { TravelerDocumentMatchList } from '@frontend/features/travelers/components/TravelerDocumentMatchList';
import { captureTravelerMatches } from './matches';
import { CaptureReviewSources } from './CaptureReviewSources';
import { CaptureField } from './CaptureField';
import { useApplyCapture } from './queries';
import {
  buildCaptureApplication,
  captureApplicationReady,
  captureRetryMustKeepInput,
  captureReviewValues,
  documentFields,
  CaptureReviewValues,
} from './review';

const DocumentMatches = ({
  caseId,
  number,
  disabled,
  onUse,
}: {
  caseId: number;
  number: string;
  disabled: boolean;
  onUse: (match: TravelerDocumentMatchDto, number: string) => void;
}) => {
  const { t } = useTranslation();
  const matches = useTravelerDocumentMatches(caseId, number);
  if (matches.isError) return <Alert severity="warning">{t('travelers.documentMatches.lookupError')}</Alert>;
  if (!matches.data?.length) return null;
  return (
    <TravelerDocumentMatchList
      matches={matches.data}
      busy={disabled}
      actionLabel={t('copilot.capture.chooseExisting')}
      onUse={(match) => onUse(match, number)}
    />
  );
};

export const CaptureReview = ({
  caseId,
  proposal,
  onDone,
}: {
  caseId: number;
  proposal: TravelerCaptureProposal;
  onDone: () => void;
}) => {
  const { t } = useTranslation();
  const roster = useAllCaseTravelers(caseId);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [panel, setPanel] = useState(0);
  const [source, setSource] = useState<TravelerCaptureSource | undefined>(proposal.sources[0]);
  const [identityChosen, setIdentityChosen] = useState(false);
  const matches = captureTravelerMatches(proposal.candidate, roster.data?.items ?? []);
  const onSource = (next: TravelerCaptureSource) => {
    setSource(next);
    setPanel(1);
  };
  // Snapshot the reviewed profile. A background refetch must not silently refresh the revision.
  const [target, setTarget] = useState<TravelerResponseDto>();
  const [link, setLink] = useState<{ match: TravelerDocumentMatchDto; number: string }>();
  const [uncertain, setUncertain] = useState(false);
  const submitted = useRef<ApplyTravelerCaptureDto>();
  const mutation = useApplyCapture(caseId, proposal.id);
  const { control, watch, reset, handleSubmit, setValue } = useForm<CaptureReviewValues>({
    defaultValues: captureReviewValues(proposal),
  });
  const values = watch();
  const busy = mutation.isLoading || uncertain;
  const input = link
    ? {
      version: proposal.version,
      action: 'link' as const,
      travelerId: link.match.travelerId,
      documentNumber: link.number,
      data: {},
    }
    : buildCaptureApplication(proposal, values, target);

  const selectTarget = (id: string) => {
    const selected = roster.data?.items.find((item) => item.id === id);
    setTarget(selected);
    setIdentityChosen(true);
    setLink(undefined);
    reset(captureReviewValues(proposal, selected));
    mutation.reset();
  };
  const useMatch = (match: TravelerDocumentMatchDto, number: string) => {
    const existing = roster.data?.items.find((item) => item.id === match.travelerId);
    if (existing) selectTarget(existing.id);
    else {
      setLink({ match, number });
      setIdentityChosen(true);
      mutation.reset();
    }
  };
  const submit = async () => {
    if (
      !submitted.current &&
      (!identityChosen || !captureApplicationReady(input) || roster.isLoading || roster.isError)
    ) return;
    const request = submitted.current ?? input;
    submitted.current = request;
    try {
      await mutation.mutateAsync(request);
      onDone();
    } catch (error) {
      const keep = captureRetryMustKeepInput(error);
      setUncertain(keep);
      if (!keep) submitted.current = undefined;
    }
  };
  const status = (mutation.error as { response?: { status?: number } })?.response?.status;
  return (
    <Box
      component="form"
      onSubmit={handleSubmit(submit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        '& .MuiInputBase-input': { fontSize: 16 },
      }}
    >
      {mobile && (
        <Tabs value={panel} onChange={(_, value) => setPanel(value)} variant="fullWidth">
          <Tab label={t('copilot.capture.dataPanel')} />
          <Tab label={t('copilot.capture.sourcePanel')} />
        </Tabs>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: mobile ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 3,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ overflowY: 'auto', minWidth: 0, pr: 1, display: mobile && panel !== 0 ? 'none' : 'block' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('copilot.capture.reviewHint')}
          </Alert>
          {matches.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{t('copilot.capture.possibleMatches')}</Typography>
              {matches.map((match) => (
                <Paper
                  key={match.traveler.id}
                  variant="outlined"
                  sx={{ p: 1.5, mt: 1, borderColor: target?.id === match.traveler.id ? 'primary.main' : 'divider' }}
                >
                  <Typography fontWeight={600}>{travelerDisplayName(match.traveler)}</Typography>
                  <Typography variant="caption" display="block">
                    {t(match.documentMatch ? 'copilot.capture.sameDocument' : 'copilot.capture.similarName')}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {match.traveler.birthDate || '—'} ·{' '}
                    {match.traveler.documents
                      .map(
                        (d) =>
                          `${t(`travelers.documents.types.${d.type}`, { defaultValue: d.type })} ${maskDocumentNumber(d.number)}`,
                      )
                      .join(' · ') || t('copilot.capture.noRegisteredDocuments')}
                  </Typography>
                  {match.birthDateConflict && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {t('copilot.capture.birthDateConflict')}
                    </Typography>
                  )}
                  <Button disabled={busy} onClick={() => selectTarget(match.traveler.id)}>
                    {t('copilot.capture.reviewExisting')}
                  </Button>
                </Paper>
              ))}
            </Box>
          )}
          <TextField
            select
            fullWidth
            label={t('copilot.capture.destination')}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={!identityChosen ? '__choose__' : link ? '__link__' : (target?.id ?? '__new__')}
            disabled={busy || roster.isLoading || roster.isError}
            onChange={(event) => selectTarget(event.target.value)}
          >
            <MenuItem value="__choose__" disabled>
              {t('copilot.capture.chooseIdentity')}
            </MenuItem>
            <MenuItem value="__new__">{t('copilot.capture.createNew')}</MenuItem>
            {link && <MenuItem value="__link__">{travelerDisplayName(link.match)}</MenuItem>}
            {(roster.data?.items ?? []).map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {travelerDisplayName(item)}
              </MenuItem>
            ))}
          </TextField>
          {roster.isError && (
            <Alert severity="error" action={<Button onClick={() => roster.refetch()}>{t('common.retry')}</Button>}>
              {t('travelers.states.loadError')}
            </Alert>
          )}
          {link ? (
            <Alert severity="info" sx={{ my: 2 }}>
              {t('copilot.capture.linkOnly', { name: travelerDisplayName(link.match) })}
              <Button
                disabled={busy}
                onClick={() => {
                  setLink(undefined);
                  setIdentityChosen(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </Alert>
          ) : (
            <>
              {TRAVELER_CAPTURE_PROFILE_FIELDS.map((key) => (
                <Controller
                  key={key}
                  control={control}
                  name={`fields.${key}`}
                  render={({ field }) => (
                    <CaptureField
                      label={t(`travelers.fields.${key}`)}
                      field={proposal.candidate.fields[key]}
                      value={field.value}
                      onChange={field.onChange}
                      kind={['nationality', 'countryOfResidence'].includes(key) ? 'country' : undefined}
                      current={target?.[key]}
                      verify={key === 'birthDate'}
                      disabled={busy}
                      sources={proposal.sources}
                      onSource={onSource}
                      hint={
                        key === 'birthDate'
                          ? t('copilot.capture.dateHint')
                          : ['nationality', 'countryOfResidence'].includes(key)
                            ? t('copilot.capture.countryHint')
                            : undefined
                      }
                    />
                  )}
                />
              ))}
              <Typography variant="h6" sx={{ mt: 2 }}>
                {t('travelers.documents.title')}
              </Typography>
              {values.documents.map((document, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, my: 1 }}>
                  <FormControlLabel
                    label={t('copilot.capture.verifyDocument', { number: index + 1 })}
                    control={
                      <Checkbox
                        checked={document.selected}
                        disabled={busy}
                        onChange={(_, checked) => setValue(`documents.${index}.selected`, checked)}
                      />
                    }
                  />
                  {target && (
                    <TextField
                      select
                      fullWidth
                      label={t('copilot.capture.documentDestination')}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                      value={document.id}
                      disabled={busy}
                      onChange={(event) => {
                        setValue(`documents.${index}.id`, event.target.value);
                        setValue(`documents.${index}.selected`, false);
                        // Choosing an existing document requires explicit selection of its optional metadata.
                        setValue(`documents.${index}.fields.issuingCountry.selected`, false);
                        setValue(`documents.${index}.fields.expiresOn.selected`, false);
                      }}
                    >
                      <MenuItem value="">{t('copilot.capture.addDocument')}</MenuItem>
                      {target.documents.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.type} · {maskDocumentNumber(item.number)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {documentFields.map((key) => (
                    <Controller
                      key={key}
                      control={control}
                      name={`documents.${index}.fields.${key}`}
                      render={({ field }) => (
                        <CaptureField
                          label={t(`travelers.documents.${key === 'issuingCountry' ? 'country' : key}`)}
                          field={proposal.candidate.documents[index][key]}
                          kind={key === 'issuingCountry' ? 'country' : key === 'type' ? 'documentType' : undefined}
                          value={field.value}
                          onChange={(next) => {
                            field.onChange(next);
                            if (key === 'type' || key === 'number') setValue(`documents.${index}.selected`, false);
                          }}
                          required={key === 'type' || key === 'number'}
                          verify={key === 'expiresOn'}
                          disabled={busy}
                          current={target?.documents.find((item) => item.id === document.id)?.[key]}
                          sources={proposal.sources}
                          onSource={onSource}
                          hint={key === 'expiresOn' ? t('copilot.capture.dateHint') : undefined}
                        />
                      )}
                    />
                  ))}
                  <DocumentMatches
                    caseId={caseId}
                    number={document.fields.number.value}
                    disabled={busy || roster.isLoading}
                    onUse={useMatch}
                  />
                </Paper>
              ))}
            </>
          )}
        </Box>
        <Box
          sx={{
            overflowY: 'auto',
            minWidth: 0,
            borderLeft: mobile ? 0 : 1,
            borderColor: 'divider',
            pl: mobile ? 0 : 2,
            display: mobile && panel !== 1 ? 'none' : 'block',
          }}
        >
          <CaptureReviewSources caseId={caseId} sources={proposal.sources} selected={source} onSelect={setSource} />
        </Box>
      </Box>
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          pt: 1.5,
          mt: 1.5,
          flexShrink: 0,
          maxHeight: '32vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: mobile ? 'column' : 'row',
          gap: 2,
          alignItems: mobile ? 'stretch' : 'center',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" aria-live="polite">
            {t(
              !identityChosen
                ? 'copilot.capture.chooseIdentity'
                : link
                  ? 'copilot.capture.confirmLink'
                  : target
                    ? 'copilot.capture.summaryUpdate'
                    : 'copilot.capture.summaryCreate',
              { name: target ? travelerDisplayName(target) : '' },
            )}
          </Typography>
          {!link && (
            <>
              <Typography variant="caption" display="block">
                {t('copilot.capture.summaryFields', {
                  fields:
                    Object.keys(input.data)
                      .filter((key) => key !== 'documents')
                      .map((key) => t(`travelers.fields.${key}`))
                      .join(', ') || '—',
                })}
              </Typography>
              <Typography variant="caption" display="block">
                {t('copilot.capture.summaryDocuments', {
                  count: input.data.documents?.length ?? 0,
                  existing: target?.documents.length ?? 0,
                })}
              </Typography>
              {values.documents.some((document) => !document.selected) && (
                <Typography variant="caption" color="warning.main" display="block">
                  {t('copilot.capture.omittedDocuments', {
                    count: values.documents.filter((document) => !document.selected).length,
                  })}
                </Typography>
              )}
            </>
          )}
          {mutation.isError && (
            <Alert severity="error" sx={{ my: 2 }}>
              {t(
                uncertain
                  ? 'copilot.capture.uncertain'
                  : status === 409
                    ? 'copilot.capture.conflict'
                    : 'copilot.capture.saveError',
              )}
            </Alert>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            type="submit"
            disabled={
              mutation.isLoading ||
              (!uncertain && (!identityChosen || !captureApplicationReady(input) || roster.isLoading || roster.isError))
            }
          >
            {t(uncertain ? 'common.retry' : link ? 'copilot.capture.confirmLink' : 'copilot.capture.confirm')}
          </Button>
          <Button disabled={mutation.isLoading} onClick={onDone}>
            {t('common.close')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
